import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { helpUrl, manualBase, resolveHelpPath, type HelpMap, type RuntimeConfig } from "./contextHelp";

/** Runtime config file served next to the app (replaced per deployment, never bundled). */
export const runtimeConfigUrl = "/runtime-config.json";

async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T | null> {
  try {
    const response = await fetch(url, { signal, credentials: "omit" });
    return response.ok ? ((await response.json()) as T) : null;
  } catch {
    // Offline, blocked by CORS or not deployed: context help falls back to the manual's home page.
    return null;
  }
}

/**
 * Opens the user manual's page of the current screen (Shell Bar «مساعدة» and F1): the route pattern of the deepest
 * route match is looked up in the manual's `help-map.json`, fetched once per session.
 */
export function useContextHelp(): { open: () => void; url: () => string } {
  const { i18n } = useTranslation();
  const routeId = useRouterState({ select: (state) => state.matches.at(-1)?.routeId ?? null });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const config = useQuery({
    queryKey: ["runtime-config"],
    queryFn: ({ signal }) => fetchJson<RuntimeConfig>(runtimeConfigUrl, signal),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const base = manualBase(config.data, i18n.language, import.meta.env.VITE_MANUAL_URL);
  const map = useQuery({
    queryKey: ["help-map", base],
    queryFn: ({ signal }) => fetchJson<HelpMap>(helpUrl(base, "help-map.json"), signal),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    enabled: !config.isPending,
  });
  const url = useCallback(
    () => helpUrl(base, resolveHelpPath(map.data, routeId === "__root__" ? null : routeId, pathname)),
    [base, map.data, pathname, routeId],
  );
  const open = useCallback(() => {
    window.open(url(), "_blank", "noopener,noreferrer");
  }, [url]);
  return { open, url };
}
